import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import {
  AnalysisViewFragment,
  GetAnalysisByTokenQuery,
  useGetAnalysisByTokenQuery,
} from 'generated/graphql';
import { useNavigate } from 'react-router-dom';
import ErrorCode from 'v2/core/errors/codes';
import { AndOfErrors, isErrorCode } from 'v2/core/errors/errors';
import { apolloStatusToQueryStatus } from 'v2/core/repository/edge-server/statusConvertor';
import { AndStatuses, QueryStatus } from 'v2/core/repository/edge-server/types';
import { getAnalysisRoute } from 'v2/core/services/analysis/link';
import useGetAnalysisDeps from 'v2/core/services/analysis/useGetAnalysisDeps';
import useRedirectIf from 'v2/core/services/redirect/useRedirectIf';
import useParseSavedSlugInfo from 'v2/core/services/savedSlug/useParseSavedSlugInfo';
import { Response } from 'v2/core/services/types';

interface useGetAnalysisFromURLParams {
  isReportingRoute?: boolean;
}

export const useGetAnalysisFromURL = ({
  isReportingRoute = false,
}: useGetAnalysisFromURLParams = {}): Response<AnalysisViewFragment> => {
  const navigate = useNavigate();
  const { token, savedSlug: analysisSlug } = useParseSavedSlugInfo();

  const { showErrorNotification } = useNotificationContext();

  const {
    data: {
      user: { id: userID },
      tenantID,
      sourceDetailsMap,
    },
    status: dependenciesStatus,
    error: dependenciesError,
  } = useGetAnalysisDeps();

  const onGetAnalysisByTokenQueryCompleted = (
    data: GetAnalysisByTokenQuery,
  ) => {
    const analysis = data?.getAnalysisByToken;
    const route =
      analysis &&
      getAnalysisRoute({
        name: analysis.title,
        token: analysis.viewToken,
        useCase: analysis.useCase,
        isReportingRoute,
      });

    // TODO, remove it post 100% rollout of Searchv2.0 Feature flag
    const isCompareRoute = route?.includes('/compare/');
    const isAnalysisRoute = route?.includes('/analysis/');

    const pathname =
      route && (isCompareRoute || isAnalysisRoute)
        ? `/explore${
            isAnalysisRoute ? route.replace('/analysis/', '/quantify/') : route
          }`
        : route;

    if (!pathname || window.location.pathname.endsWith(pathname)) {
      return;
    }
    navigate(`${pathname}${window.location.search}`);
  };

  const {
    data,
    error: analysisError,
    loading,
    called,
  } = useGetAnalysisByTokenQuery({
    variables: {
      token: token ?? '',
      tenantID,
      userID,
      includeScheduleOutbounds: true,
    },
    fetchPolicy: 'cache-and-network',
    skip: !token || !sourceDetailsMap,
    onCompleted: onGetAnalysisByTokenQueryCompleted,
  });

  const analysis = data?.getAnalysisByToken;

  const analysisStatus = apolloStatusToQueryStatus(
    loading,
    called,
    (!token || !sourceDetailsMap) ?? false,
    analysisError,
  );

  const isRecordNotFoundError = isErrorCode(
    analysisError,
    ErrorCode.RecordNotFound,
  );
  useRedirectIf(
    // if we have analysis slug and failed to parse id or token
    (analysisSlug && !token) ||
      // or if we have token and id but we didn't get any record matching
      isRecordNotFoundError,
    {
      redirectRoute: '/thematic-analysis/view-data',
      onRedirect: () =>
        showErrorNotification({
          contentComponent: `Invalid Quantify Link`,
          props: {},
        }),
    },
  );

  const status = token
    ? AndStatuses([dependenciesStatus, analysisStatus])
    : // if we don't have token, we are done
      QueryStatus.SUCCESS_COMPLETED;
  const error = AndOfErrors([
    dependenciesError,
    !isRecordNotFoundError ? analysisError : undefined,
  ]);

  return {
    data: analysis,
    status,
    error,
  };
};
